/**
 * 公共费用分摊页面
 * 费用录入 + 分摊功能的组合页面
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Space, message, Row, Col, Alert, Spin, Radio, Tooltip, Modal } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import CostInfoForm from "./components/CostInfoForm";
import AllocationPreview from "./components/AllocationPreview";
import TargetLegTable from "./components/TargetLegTable";
import { settlementApi } from "../../../api/settlementApi";
import { refreshSettlementBatchPending } from "../../../utils/settlementBatchPending";
import { computeAllocation, summarizeHeaderRate, round2 } from "./utils/computeAllocation";

/**
 * 公共费用分摊主页面组件
 */
const CostAllocationPage = () => {
    // 状态管理
    const [selectedTargetLegs, setSelectedTargetLegs] = useState([]); // 选中的目标航段
    const [currentCost, setCurrentCost] = useState(null); // 当前录入的费用数据
    const [loading, setLoading] = useState(false);
    const [importMode, setImportMode] = useState("overwrite"); // overwrite=覆盖 accumulate=叠加
    
    // 汇率验证状态
    const [rateValidation, setRateValidation] = useState({
        isValidated: false,
        allValid: false,
        validLegs: [],
        invalidLegs: [],
        loading: false,
        error: null,
        validationTime: null
    });

    // 执行汇率验证
    const validateExchangeRates = useCallback(async () => {
        if (!currentCost?.currency || selectedTargetLegs.length === 0) {
            setRateValidation(prev => ({ ...prev, isValidated: false, allValid: false }));
            return;
        }

        // ✨ 优化：如果币种是USD，不需要调用汇率API
        // 因为数据库存储的就是USD金额，USD→USD汇率固定为1.0
        if (currentCost.currency === 'USD') {
            console.log(`✅ 费用币种为USD，无需汇率转换，直接通过验证 (${selectedTargetLegs.length}个航段)`);
            setRateValidation({
                isValidated: true,
                allValid: true,  // USD→USD 汇率固定为1.0，始终有效
                validLegs: selectedTargetLegs.map(leg => ({ leg_id: leg.id, rate: 1.0, exchange_rate_id: null })),
                invalidLegs: [],
                loading: false,
                error: null,
                validationTime: new Date().toISOString()
            });
            return;
        }

        // 非USD币种需要调用API验证汇率
        setRateValidation(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            console.log("执行汇率验证:", {
                currency: currentCost.currency,
                legsCount: selectedTargetLegs.length
            });

            const response = await settlementApi.costAllocation.validateAllocationRates({
                from_currency: currentCost.currency,
                to_currency: 'USD',
                target_legs: selectedTargetLegs.map(leg => ({
                    leg_id: leg.id,
                    flight_number: leg.flightNumber,
                    flight_date: leg.flightDate
                }))
            });

            if (response.data.success) {
                const { data } = response.data;
                setRateValidation({
                    isValidated: true,
                    allValid: data.all_valid,
                    validLegs: data.valid_legs || [],
                    invalidLegs: data.invalid_legs || [],
                    loading: false,
                    error: null,
                    validationTime: new Date().toISOString()
                });

                if (data.all_valid) {
                    console.log(`✅ 汇率验证通过: ${data.valid_count} 个航段`);
                } else {
                    console.warn(`⚠️ 汇率验证失败: ${data.invalid_count} 个航段缺少汇率`);
                }
            } else {
                throw new Error(response.data.message || '汇率验证失败');
            }

        } catch (error) {
            console.error('汇率验证API调用失败:', error);
            setRateValidation(prev => ({
                ...prev,
                loading: false,
                error: error.message || '汇率验证失败',
                isValidated: false,
                allValid: false
            }));
        }
    }, [currentCost?.currency, selectedTargetLegs]);

    // 处理费用信息变化
    const handleCostChange = (costData) => {
        setCurrentCost(costData);
        console.log("费用信息变更:", costData);
    };

    // 处理目标航段选择变化
    const handleTargetSelection = (selectedRowKeys, selectedRows) => {
        setSelectedTargetLegs(selectedRows);
        console.log("选中的目标航段:", selectedRows);
    };


    const isBhMode = currentCost?.allocationMethod === "BY_BLOCK_HOURS";

    // 判断是否可以执行分摊（平均分摊看金额，BH×单价看单价）
    const canAllocate = useMemo(() => {
        const priceValid = isBhMode ? currentCost?.unitPrice > 0 : currentCost?.amount > 0;
        return currentCost &&
               currentCost.supplier &&
               currentCost.costType &&
               priceValid &&
               selectedTargetLegs.length > 0 &&
               rateValidation.isValidated &&
               rateValidation.allValid &&  // 关键：必须所有汇率都有效
               !loading;
    }, [currentCost, isBhMode, selectedTargetLegs, rateValidation, loading]);

    // 切换分摊方式时清空已选航段：两种方式 BH 数据源不同，航段不通用
    useEffect(() => {
        setSelectedTargetLegs([]);
    }, [isBhMode]);

    // 当费用信息或选中航段变化时，重新验证汇率
    useEffect(() => {
        validateExchangeRates();
    }, [validateExchangeRates]);

    // 页面重新获得焦点时，重新验证汇率（用户可能从汇率管理页面返回）
    useEffect(() => {
        const handleFocus = () => {
            if (rateValidation.isValidated && !rateValidation.allValid) {
                console.log("页面重新获得焦点，重新验证汇率");
                validateExchangeRates();
            }
        };
        
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [rateValidation, validateExchangeRates]);

    // 执行费用分摊
    const submitAllocation = async () => {
        const priceValid = isBhMode ? currentCost?.unitPrice > 0 : currentCost?.amount > 0;
        if (!currentCost || !currentCost.supplier || !currentCost.costType || !priceValid) {
            message.warning("请完整填写费用信息");
            return;
        }

        if (selectedTargetLegs.length === 0) {
            message.warning("请先选择目标航段");
            return;
        }

        if (!rateValidation.isValidated || !rateValidation.allValid) {
            message.error("请确保所有选中航段的汇率都有效");
            return;
        }

        setLoading(true);
        try {
            const { results, totalOriginal, totalUsd } = computeAllocation({
                costData: currentCost,
                selectedLegs: selectedTargetLegs,
                rateValidation
            });

            if (totalUsd == null) {
                message.error("仍有航段缺少航班日汇率，无法分摊");
                setLoading(false);
                return;
            }

            const totalBHForPayload = isBhMode
                ? selectedTargetLegs.reduce((sum, leg) => sum + (parseFloat(leg.blockHours) || 0), 0)
                : null;

            if (isBhMode && totalBHForPayload <= 0) {
                message.error("所选航段的总 BH 必须大于 0");
                setLoading(false);
                return;
            }

            const allocationItems = results.map((r) => ({
                flight_operation_id: r.id,
                flight_number: r.flightNumber,
                flight_date: r.flightDate,
                allocated_usd_amount: r.allocatedUsd,
                allocated_original_amount: r.allocatedOriginal,
                allocation_weight: isBhMode ? (parseFloat(r.blockHours) || 0) : 1,
                allocation_ratio: r.ratio
            }));

            const headerRate = summarizeHeaderRate(results, totalOriginal, totalUsd);

            const response = await settlementApi.costAllocation.createAllocationEntry({
                cost_info: {
                    supplier_id: currentCost.supplier,
                    cost_type: currentCost.costType,
                    description: currentCost.description || `${currentCost.costType}费用`,
                    original_amount: totalOriginal,
                    original_currency: currentCost.currency,
                    usd_amount: totalUsd,
                    exchange_rate: headerRate,
                    is_estimate: currentCost.isEstimate || false,
                    unit_price_original: isBhMode ? currentCost.unitPrice : null,
                    unit_price_usd: isBhMode ? round2(totalUsd / totalBHForPayload) : null,
                    total_bh: totalBHForPayload
                },
                allocation_items: allocationItems,
                allocation_method: currentCost.allocationMethod || 'EQUAL_SPLIT',
                total_allocated_usd: totalUsd,
                import_mode: importMode
            });

            if (response.data.success) {
                refreshSettlementBatchPending(response.data.affected_batch_ids);
                message.success(response.data.message);
                setCurrentCost(null);
                setSelectedTargetLegs([]);
                setRateValidation({
                    isValidated: false,
                    allValid: false,
                    validLegs: [],
                    invalidLegs: [],
                    loading: false,
                    error: null,
                    validationTime: null
                });
            } else {
                throw new Error(response.data.message || "分摊失败");
            }

        } catch (error) {
            console.error("费用分摊失败:", error);
            let errorMessage = "操作失败";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const describeLegs = (legs = []) => {
        const labels = legs.slice(0, 8).map((leg) => {
            const aircraft = leg.aircraft_reg ? `${leg.aircraft_reg} ` : "";
            const flight = leg.flight_number || "";
            const date = leg.flight_date || "";
            return `${aircraft}${flight} ${date}`.trim();
        });
        const more = legs.length > 8 ? ` 等 ${legs.length} 段` : "";
        return labels.join("、") + more;
    };

    const handleConfirmAllocation = async () => {
        const priceValid = isBhMode ? currentCost?.unitPrice > 0 : currentCost?.amount > 0;
        if (!currentCost || !currentCost.supplier || !currentCost.costType || !priceValid) {
            message.warning("请完整填写费用信息");
            return;
        }
        if (selectedTargetLegs.length === 0) {
            message.warning("请先选择目标航段");
            return;
        }
        if (!rateValidation.isValidated || !rateValidation.allValid) {
            message.error("请确保所有选中航段的汇率都有效");
            return;
        }

        try {
            const previewRes = await settlementApi.costAllocation.precheckAllocationEntry({
                supplier_id: currentCost.supplier,
                cost_type: currentCost.costType,
                flight_ids: selectedTargetLegs.map((leg) => leg.id),
                import_mode: importMode,
                original_currency: currentCost.currency,
            });
            const preview = previewRes.data?.data || {};
            const kept = preview.kept_legs || [];
            const replaced = preview.replaced_legs || [];

            if (preview.currency_conflict) {
                Modal.error({
                    title: "不能换币种覆盖",
                    content: `这张单上还有未勾选的航班，原来是 ${preview.existing_currency || "原币种"}。换币种会把两种钱加在一起。请勾上这张单上剩下的航班，或继续用原来的币种。`,
                });
                return;
            }

            if (importMode === "accumulate" && preview.will_double_count) {
                Modal.confirm({
                    title: "叠加会在这些航段上再记一笔",
                    content: `这些航段已有同类分摊：${describeLegs(replaced)}。确认后会变成两笔，对账时金额会加两次。`,
                    okText: "确认叠加",
                    cancelText: "取消",
                    onOk: submitAllocation,
                });
                return;
            }

            if (importMode === "overwrite" && (kept.length > 0 || replaced.length > 0)) {
                const keptTotal = kept.reduce((sum, leg) => sum + (Number(leg.amount) || 0), 0);
                const lines = [];
                if (replaced.length > 0) {
                    lines.push(`将改写 ${replaced.length} 段已有分摊：${describeLegs(replaced)}`);
                }
                if (kept.length > 0) {
                    lines.push(`未勾选的 ${kept.length} 段会留在原单上：${describeLegs(kept)}，约 $${keptTotal.toFixed(2)}。`);
                    lines.push("这张单的总数会变成「留下的 + 你这次填的」，不是把整张单改成这次填写的金额。");
                } else if (replaced.length > 0) {
                    lines.push("这次勾选覆盖这些旧段的金额，其它飞机不受影响。");
                }
                Modal.confirm({
                    title: "确认覆盖本次勾选",
                    content: (
                        <div>
                            {lines.map((line) => (
                                <p key={line} style={{ marginBottom: 8 }}>{line}</p>
                            ))}
                        </div>
                    ),
                    okText: "确认覆盖",
                    cancelText: "取消",
                    onOk: submitAllocation,
                    width: 560,
                });
                return;
            }
        } catch (error) {
            console.error("分摊预检失败:", error);
            message.warning("预检失败，仍可继续提交。请再确认勾选范围。");
        }

        await submitAllocation();
    };


    return (
        <div className="page-container">
            {/* 页面头部 */}
            <div className="page-header-compact">
                <div className="page-title-compact">公共费用</div>
                <div className="page-action-compact">
                    <Space>
                        <Tooltip
                            title={
                                importMode === "overwrite"
                                    ? "只改这次勾上的航班。没勾的仍留在原单上，单的总数会变成留下的加这次填的，不是整张单改成你填的那个数。"
                                    : "再记一笔。若勾选航段上已有同类分摊，金额会加两次，提交前会再确认。"
                            }
                        >
                            <Radio.Group
                                value={importMode}
                                onChange={(e) => setImportMode(e.target.value)}
                                optionType="button"
                                buttonStyle="solid"
                                size="small"
                            >
                                <Radio.Button value="overwrite">覆盖本次勾选</Radio.Button>
                                <Radio.Button value="accumulate">叠加</Radio.Button>
                            </Radio.Group>
                        </Tooltip>
                        <PermissionGuard permission="settlement:allocate">
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                onClick={handleConfirmAllocation}
                                loading={loading}
                                disabled={!canAllocate}
                            >
                                确认录入并分摊
                                {rateValidation.isValidated && !rateValidation.allValid && " (汇率不完整)"}
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            {/* 汇率验证状态显示 */}
            {rateValidation.loading && (
                <Alert
                    message={<><Spin size="small" style={{ marginRight: 8 }} />正在验证汇率...</>}
                    type="info"
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* 汇率验证成功提示 */}
            {rateValidation.isValidated && rateValidation.allValid && selectedTargetLegs.length > 0 && (
                <Alert
                    message="汇率验证通过"
                    description={`已验证 ${selectedTargetLegs.length} 个航段的 ${currentCost?.currency}/USD 汇率，可以执行分摊`}
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* 全局汇率状态概览 */}
            {rateValidation.isValidated && !rateValidation.allValid && rateValidation.invalidLegs.length > 0 && (
                <Alert
                    message={`汇率验证: ${rateValidation.invalidLegs.length} 个航段缺少有效汇率`}
                    description="请在下方分摊预览中查看具体航段并设置对应汇率"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* 汇率验证错误提示 */}
            {rateValidation.error && (
                <Alert
                    message="汇率验证失败"
                    description={rateValidation.error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* 主要内容区域 - 左右布局 */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
                {/* 左侧：费用信息 */}
                <Col span={12}>
                    <Card title="费用信息" size="small">
                        <CostInfoForm 
                            onCostChange={handleCostChange}
                        />
                    </Card>
                </Col>
                
                {/* 右侧：分摊预览 */}
                <Col span={12}>
                    <Card title="分摊预览" size="small">
                        <AllocationPreview
                            costData={currentCost}
                            selectedLegs={selectedTargetLegs}
                            rateValidation={rateValidation}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 下方：航段选择 */}
            <Card title="航段选择" size="small">
                <TargetLegTable
                    selectedRowKeys={selectedTargetLegs.map(leg => leg.id)}
                    selectedLegs={selectedTargetLegs}
                    onSelectionChange={handleTargetSelection}
                    useAuthoritativeBH={isBhMode}
                    validateBH={isBhMode}
                    showValidationStatus={isBhMode}
                />
            </Card>
        </div>
    );
};


export default CostAllocationPage;
